import classNames from "classnames";
import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import {
  DragDropContext,
  Draggable,
  Droppable,
  OnDragEndResponder,
} from "react-beautiful-dnd";
import { IoTimeOutline } from "react-icons/io5";
import {
  LuCalendar,
  LuChevronDown,
  LuBriefcase,
  LuCreditCard,
  LuTrash2,
} from "react-icons/lu";
import { RxDragHandleDots2 } from "react-icons/rx";
import { useDebounce } from "usehooks-ts";

import { ConfirmNavigation } from "../../../../components/ConfirmNavigation";
import CustomCurrencyInput from "../../../../components/CustomCurrencyInput";
import { StyledDatePicker } from "../../../../components/datepicker/datepicker";
import { SettingsConfirmModal } from "../../../../components/modal/SettingsConfirmModal";
import { WarningExclaimSvg } from "../../../../components/svg/WarningExclaimSvg";
import { useUnsavedChangesGuard } from "../../../../components/UnsavedChangesGuard";
import {
  getInitials,
  getInitialsFromFullName,
} from "../../../../constants/constants";
import { CustomerModel } from "../../../../factories/customers/models/customerModel";
import useOnClickOutside from "../../../../hooks/useOnClickOutside";
import { useAppDispatch, useAppSelector } from "../../../../redux/redux";
import { billingAPI } from "../../../../services/BillingService";
import { getLinkedFirm } from "../../../../sessionStorage/sessionStorage";
import { useGetActiveCustomersQuery } from "../../../../slices/ContactsSlice";
import { useGetWorksByCustomerIdMutation } from "../../../../slices/UserSlice";
import { useGetWorksQuery } from "../../../../slices/WorkSlice";
import { SelectOptionType } from "../../../../types/types";
import { transformDateToUTC } from "../../../../utils/transformDate";
import { InvoiceTime, invoiceableTimes } from "../../../time/InvoiceTime";
import { WorkTableSort } from "../../../works/types/work-table";
import {
  BILLING_DATE_PICKER_FORMAT,
  DUE_DATE_ERROR,
  isDueDateBeforeInvoiceDate,
} from "../../billingDates.utils";
import {
  BILLING_CARD,
  BILLING_PRIMARY_BUTTON,
  BILLING_SECONDARY_BUTTON,
} from "../../ui/BillingUi";
import { BillableTime } from "../BillableTime";
import { ViewWork } from "../ViewWork";
import {
  InvoiceFormItem,
  InvoiceFormProps,
  InvoiceSaveOption,
} from "./invoice-form.types";
import {
  buildInvoiceDraft,
  createDefaultInvoiceItem,
  dueDateAfterTermsChange,
  hasCustomTerm,
  INVALID_DATE,
  invoiceAmountProblem,
  termAfterDueDateChange,
  formatMoney,
  isInvoiceNumberInUse,
  mapBillableTimeToInvoiceItem,
  parseMoney,
  shouldCheckInvoiceNumber,
  totalRate,
} from "./invoice-form.utils";
import { InvoiceClientPicker } from "./InvoiceClientPicker";
import { linkLineToWork, workOptionsFor } from "./invoiceWork.utils";
import { useInvoiceTotals } from "./useInvoiceTotals";

const CONTACTS_PAGE_SIZE = 20;
const DEFAULT_CONTACT_SORT_COLUMN = "Name";
const DEFAULT_CONTACT_SORT_ORDER: "asc" | "desc" = "asc";

const uniqueOptionsByValue = (options: SelectOptionType[]) =>
  options.reduce<SelectOptionType[]>((acc, option) => {
    if (!option?.value) {
      return acc;
    }

    const alreadyExists = acc.some((item) => item.value === option.value);

    if (!alreadyExists) {
      acc.push(option);
    }

    return acc;
  }, []);

const mapCustomerToOption = (customer: CustomerModel): SelectOptionType => {
  const avatarColor: SelectOptionType["avatarColor"] =
    customer.businessContactId ? "purple" : "blue";

  return {
    value: customer.customerId,
    label: `${customer.customerName}${
      customer.isSyncedWithQBO ? " (synced)" : ""
    }`,
    avatar: getInitialsFromFullName(customer.customerName),
    avatarColor,
  };
};

export const InvoiceForm: FC<InvoiceFormProps> = ({
  mode,
  formKey,
  title,
  defaultCustomerId = "",
  defaultWorkId = "",
  defaultAllTime = false,
  initialValues,
  terms,
  services,
  taxRateData,
  users,
  isSubmitting = false,
  paymentsReceived = 0,
  showSaveAndPay = false,
  submitLabel = "Save",
  existingInvoiceItems,
  footer,
  onCancel,
  onSubmit,
}) => {
  const { user } = useAppSelector((state) => state.appReducer);
  const dispatch = useAppDispatch();
  const orgId = getLinkedFirm()?.orgId || "";

  const [currentContact, setCurrentContact] = useState<SelectOptionType | null>(
    initialValues.currentContact,
  );
  const [invoiceNo, setInvoiceNo] = useState(initialValues.invoiceNo);
  const [invoiceCustomers, setInvoiceCustomers] = useState<SelectOptionType[]>(
    initialValues.invoiceCustomers,
  );
  const [invoiceDate, setInvoiceDate] = useState(initialValues.invoiceDate);
  const [dueDate, setDueDate] = useState(initialValues.dueDate);
  const [term, setTerm] = useState(initialValues.term);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(
    initialValues.selectedUserId,
  );
  const [isIncludeTax, setIsIncludeTax] = useState(initialValues.isIncludeTax);
  const [discount, setDiscount] = useState(initialValues.discount);
  const [discountType, setDiscountType] = useState<"$" | "%">(
    initialValues.discountType,
  );
  const [notes, setNotes] = useState(initialValues.notes);
  const [data, setData] = useState<InvoiceFormItem[]>(initialValues.items);
  const [taxRateValue, setTaxRateValue] = useState(initialValues.taxRateValue);

  const [search, setSearch] = useState("");
  const [contactsPage, setContactsPage] = useState(1);
  const [contactOptions, setContactOptions] = useState<SelectOptionType[]>([]);
  const [customerId, setCustomerId] = useState(defaultCustomerId);

  const [isOpenSaveMenu, setIsOpenSaveMenu] = useState(false);
  const [isBillableTime, setIsBillableTime] = useState(false);
  const [collapsedItems, setCollapsedItems] = useState<string[]>([]);
  const [billedWorks, setBilledWorks] = useState<InvoiceTime[]>([]);
  const [viewWork, setViewWork] = useState(false);
  const [workItem, setWorkItem] = useState<InvoiceFormItem>();
  const [isSaving, setIsSaving] = useState(false);
  const [showDueDateError, setShowDueDateError] = useState(false);
  // BILL-030 (QA batch 17): a typed date that isn't a date says so.
  const [dateProblem, setDateProblem] = useState<{
    field: "invoice" | "due";
    text: string;
  } | null>(null);
  const [duplicateNumberSave, setDuplicateNumberSave] =
    useState<InvoiceSaveOption | null>(null);

  const optionsRef = useRef<HTMLDivElement>(null);
  const initialDraftRef = useRef<string | null>(null);
  const isLoadingNextContactsPageRef = useRef(false);

  const debounceSearch = useDebounce(search, 300);

  const [getWorks, { data: works, isLoading: isLoadingWorks }] =
    useGetWorksByCustomerIdMutation();

  useOnClickOutside(optionsRef, () => setIsOpenSaveMenu(false));

  useEffect(() => {
    setCurrentContact(initialValues.currentContact);
    setInvoiceNo(initialValues.invoiceNo);
    setInvoiceCustomers(initialValues.invoiceCustomers);
    setInvoiceDate(initialValues.invoiceDate);
    setDueDate(initialValues.dueDate);
    setTerm(initialValues.term);
    setSelectedUserId(initialValues.selectedUserId);
    setIsIncludeTax(initialValues.isIncludeTax);
    setDiscount(initialValues.discount);
    setDiscountType(initialValues.discountType);
    setNotes(initialValues.notes);
    setData(initialValues.items);
    setTaxRateValue(initialValues.taxRateValue);
    setBilledWorks([]);
    setIsBillableTime(false);
    setViewWork(false);
    setWorkItem(undefined);
    setIsSaving(false);
    initialDraftRef.current = null;
  }, [formKey, initialValues]);

  useEffect(() => {
    setCustomerId(defaultCustomerId);
  }, [defaultCustomerId, formKey]);

  const {
    data: contacts,
    isLoading: isLoadingContacts,
    isFetching: isFetchingContacts,
  } = useGetActiveCustomersQuery(
    {
      orgId,
      isActive: true,
      userId: user?.id || "",
      tagIds: [],
      yearEndMonth: [],
      customerName: null,
      sortOrder: DEFAULT_CONTACT_SORT_ORDER,
      sortColumn: DEFAULT_CONTACT_SORT_COLUMN,
      searchQuery: debounceSearch || "",
      pageSize: CONTACTS_PAGE_SIZE,
      pageNumber: contactsPage,
      organizationTypeIds: [],
    },
    {
      refetchOnMountOrArgChange: true,
      skip: !orgId || !user?.id,
    },
  );

  useEffect(() => {
    if (!isFetchingContacts) {
      isLoadingNextContactsPageRef.current = false;
    }
  }, [isFetchingContacts]);

  useEffect(() => {
    setContactsPage(1);
    setContactOptions([]);
    isLoadingNextContactsPageRef.current = false;
  }, [debounceSearch, orgId, user?.id]);

  useEffect(() => {
    const nextOptions: SelectOptionType[] = (contacts || [])
      .map(mapCustomerToOption)
      .filter((option) => option.value);

    setContactOptions((prev) =>
      contactsPage === 1
        ? uniqueOptionsByValue(nextOptions)
        : uniqueOptionsByValue([...prev, ...nextOptions]),
    );
  }, [contacts, contactsPage]);

  const selectedContacts = useMemo<SelectOptionType[]>(
    () =>
      uniqueOptionsByValue([
        ...(currentContact ? [currentContact] : []),
        ...invoiceCustomers,
      ]),
    [currentContact, invoiceCustomers],
  );

  const mergedContactOptions = useMemo<SelectOptionType[]>(
    () => uniqueOptionsByValue([...selectedContacts, ...contactOptions]),
    [selectedContacts, contactOptions],
  );

  const contactsTotalRecords = contacts?.[0]?.totalRecords || 0;

  const hasMoreContacts =
    contactsTotalRecords > 0
      ? contactOptions.length < contactsTotalRecords
      : (contacts?.length || 0) === CONTACTS_PAGE_SIZE;

  const isLoadingContactsFirstPage =
    (isLoadingContacts || isFetchingContacts) && contactsPage === 1;

  const isLoadingMoreContacts = isFetchingContacts && contactsPage > 1;

  const handleLoadMoreContacts = () => {
    if (
      !hasMoreContacts ||
      isFetchingContacts ||
      isLoadingNextContactsPageRef.current
    ) {
      return;
    }

    isLoadingNextContactsPageRef.current = true;
    setContactsPage((prev) => prev + 1);
  };

  const handleSelectedContactsChange = (newValue: unknown) => {
    const selected = uniqueOptionsByValue(
      Array.isArray(newValue)
        ? (newValue as SelectOptionType[])
        : newValue
        ? [newValue as SelectOptionType]
        : [],
    );

    const [mainCustomer, ...additionalCustomers] = selected;

    setCurrentContact(mainCustomer || null);
    setInvoiceCustomers(additionalCustomers);
  };

  useEffect(() => {
    if (!customerId || !contactOptions.length || isLoadingContactsFirstPage) {
      return;
    }

    const defaultContact =
      contactOptions.find(
        (item) => String(item.value).trim() === String(customerId).trim(),
      ) || null;

    if (!defaultContact) {
      return;
    }

    setCurrentContact(defaultContact);
    setInvoiceCustomers((prev) =>
      prev.filter((item) => item.value !== defaultContact.value),
    );
    setCustomerId("");
  }, [contactOptions, customerId, isLoadingContactsFirstPage]);

  useEffect(() => {
    if (!currentContact?.value) {
      return;
    }

    getWorks({
      customerId: currentContact.value,
    });
  }, [currentContact?.value, getWorks]);

  // Work items of the invoice's client(s), for the per-line "Work item" picker.
  const selectedClientIds = useMemo(
    () => selectedContacts.map((c) => String(c.value)).filter(Boolean),
    [selectedContacts],
  );
  const { data: clientWorks } = useGetWorksQuery(
    {
      orgId,
      currentUserId: user?.id,
      customerIds: selectedClientIds,
      pageNumber: 1,
      pageSize: 300,
      sortBy: WorkTableSort.DueDate,
      sortOrder: "desc",
      hideGreenStatus: false,
    },
    { skip: !orgId || !selectedClientIds.length },
  );
  const linkableWorks = useMemo(
    () =>
      (clientWorks || []).filter((w) =>
        selectedClientIds.includes(String(w.customerId)),
      ),
    [clientWorks, selectedClientIds],
  );

  // Terms set the due date only when the person changes Terms or the invoice
  // date (QA batch 10): an effect here also ran when Edit loaded and replaced
  // the saved due date.
  const sortedServices = useMemo(
    () =>
      [...services].sort((a, b) =>
        (a.name || "").localeCompare(b.name || "", undefined, {
          sensitivity: "base",
        }),
      ),
    [services],
  );

  const firstService = sortedServices[0];

  // "Invoice" button on a work item: add that work's unbilled time, or else
  // link the first line to the work item. Done once per form. Ask ProCharted
  // (`defaultAllTime`) adds all of the client's unbilled time the same way.
  const appliedWorkRef = useRef("");
  useEffect(() => {
    if (
      (!defaultWorkId && !defaultAllTime) ||
      appliedWorkRef.current === formKey
    )
      return;
    if (!works || !clientWorks) return;
    const work = clientWorks.find((w) => w.workId === defaultWorkId);
    // Never re-add time that is already on an invoice (QA C1).
    const times = invoiceableTimes(works).filter(
      (t) => defaultAllTime || t.work?.id === defaultWorkId,
    );
    appliedWorkRef.current = formKey;
    // Lines Ask ProCharted proposed (`ai-` row ids) stay next to the time.
    const proposedLines = initialValues.items.filter((item) =>
      item.clientRowId.startsWith("ai-"),
    );
    if (times.length) {
      setData((prev) => {
        // Drop the untouched starting line; keep anything already linked.
        const kept = prev.filter(
          (item) =>
            item.invoiceType === "billedItem" ||
            item.workId ||
            proposedLines.some((line) => line.clientRowId === item.clientRowId),
        );
        return [
          ...kept,
          ...times.map((t) => mapBillableTimeToInvoiceItem(t, firstService)),
        ];
      });
      setBilledWorks(times);
      return;
    }
    if (!work) return;
    setData((prev) =>
      prev.map((item, i) =>
        i === 0
          ? {
              ...item,
              ...linkLineToWork(work.workId, clientWorks),
              description: item.description || work.workName,
            }
          : item,
      ),
    );
  }, [
    defaultWorkId,
    defaultAllTime,
    formKey,
    works,
    clientWorks,
    firstService,
    initialValues.items,
  ]);

  const { subtotal, discountValue, taxBaseAmount, taxRows, totalTaxValue, total } =
    useInvoiceTotals({
      items: data,
      discount,
      discountType,
      isIncludeTax,
      taxCodes: taxRateData,
      defaultTaxCodeId: taxRateValue?.id,
    });

  const balanceDue = total - paymentsReceived;
  const amountProblem = invoiceAmountProblem({
    items: data,
    discount,
    discountType,
  });

  const currentDraft = useMemo(
    () =>
      JSON.stringify(
        buildInvoiceDraft({
          invoiceNo,
          currentContact,
          invoiceCustomers,
          invoiceDate,
          dueDate,
          term,
          selectedUserId,
          isIncludeTax,
          discount,
          discountType,
          taxRateId: taxRateValue?.id || null,
          notes,
          data,
        }),
      ),
    [
      invoiceNo,
      currentContact,
      invoiceCustomers,
      invoiceDate,
      dueDate,
      term,
      selectedUserId,
      isIncludeTax,
      discount,
      discountType,
      taxRateValue?.id,
      notes,
      data,
    ],
  );

  const { setHasUnsavedChanges } = useUnsavedChangesGuard();

  const isDirty =
    !!initialDraftRef.current && initialDraftRef.current !== currentDraft;

  useEffect(() => {
    setHasUnsavedChanges(isDirty && !isSubmitting && !isSaving);
  }, [isDirty, isSaving, isSubmitting, setHasUnsavedChanges]);

  useEffect(() => {
    return () => {
      setHasUnsavedChanges(false);
    };
  }, [setHasUnsavedChanges]);

  useEffect(() => {
    if (!initialDraftRef.current && !isLoadingContactsFirstPage) {
      initialDraftRef.current = currentDraft;
    }
  }, [currentDraft, isLoadingContactsFirstPage]);

  const handleIncludeTaxChange = (value: boolean) => {
    setIsIncludeTax(value);
  };

  const handleAddItem = () => {
    setData((prev) => [...prev, createDefaultInvoiceItem(firstService)]);
  };

  const handleDeleteItem = (clientRowId: string) => {
    if (data.length <= 1) {
      return;
    }

    setData((prev) => prev.filter((item) => item.clientRowId !== clientRowId));
    setBilledWorks((prev) => prev.filter((item) => item.id !== clientRowId));
  };

  const handleAddWorkItems = (items: InvoiceTime[]) => {
    setData((prev) => {
      const existingIds = new Set(prev.map((item) => item.clientRowId));

      const newItems = items
        .filter((item) => !existingIds.has(item.id))
        .map((item) => mapBillableTimeToInvoiceItem(item, firstService));

      return [...prev, ...newItems];
    });

    setBilledWorks((prev) => {
      const existingIds = new Set(prev.map((item) => item.id));
      const newItems = items.filter((item) => !existingIds.has(item.id));

      return [...prev, ...newItems];
    });

    setIsBillableTime(false);
  };

  const handleDragEnd: OnDragEndResponder = (result) => {
    const { destination, source } = result;

    if (!destination || destination.index === source.index) {
      return;
    }

    const reorderedData = Array.from(data);
    const [movedItem] = reorderedData.splice(source.index, 1);

    reorderedData.splice(destination.index, 0, movedItem);
    setData(reorderedData);
  };

  /** True when another invoice of the firm already has this number. */
  const isNumberTakenByAnotherInvoice = async () => {
    if (
      !orgId ||
      !shouldCheckInvoiceNumber(mode, invoiceNo, initialValues.invoiceNo)
    ) {
      return false;
    }

    // Subscribed while it loads: with `subscribe: false` the request ended at
    // once, the catch below said "free" and the warning never showed.
    const request = dispatch(
      billingAPI.endpoints.getAllInvoices.initiate(orgId, {
        forceRefetch: true,
      }),
    );
    try {
      const invoices = await request.unwrap();

      return isInvoiceNumberInUse(invoices, invoiceNo);
    } catch {
      // The check is only a warning: if it fails, saving goes on as before.
      return false;
    } finally {
      request.unsubscribe();
    }
  };

  const submit = async (
    option: InvoiceSaveOption,
    confirmedDuplicateNumber = false,
  ) => {
    if (!currentContact || !data.length || isSubmitting || isSaving) {
      return;
    }

    // Due date can't be before the invoice date (Roadmap 1812).
    if (isDueDateBeforeInvoiceDate(invoiceDate, dueDate)) {
      setShowDueDateError(true);
      return;
    }

    // BILL-034 / BILL-041 (QA batch 17): rate / quantity / discount limits.
    if (amountProblem) {
      return;
    }

    // Number already used: warn first, QuickBooks-style (Roadmap 1816).
    if (!confirmedDuplicateNumber) {
      setIsSaving(true);
      const isTaken = await isNumberTakenByAnotherInvoice();
      setIsSaving(false);

      if (isTaken) {
        setDuplicateNumberSave(option);
        return;
      }
    }

    const values = {
      invoiceNo,
      currentContact,
      invoiceCustomers,
      invoiceDate,
      dueDate,
      term,
      selectedUserId,
      isIncludeTax,
      discount,
      discountType,
      notes,
      items: data,
      taxRateValue,
      billedWorks,
      subtotal,
      discountValue,
      totalTax: totalTaxValue,
      total,
      balanceDue,
    };

    setIsSaving(true);
    setHasUnsavedChanges(false);

    try {
      await onSubmit(values, option);
      initialDraftRef.current = currentDraft;
      setHasUnsavedChanges(false);
      setIsSaving(false);
    } catch (error) {
      setIsSaving(false);
      setHasUnsavedChanges(isDirty);
      throw error;
    }
  };

  const isSubmitDisabled =
    !currentContact || !data.length || !invoiceNo || isSubmitting;

  const updateItem = (
    clientRowId: string,
    changes: (item: InvoiceFormItem) => Partial<InvoiceFormItem>,
  ) =>
    setData((prev) =>
      prev.map((invoiceItem) =>
        invoiceItem.clientRowId === clientRowId
          ? { ...invoiceItem, ...changes(invoiceItem) }
          : invoiceItem,
      ),
    );

  const lineGrid =
    "grid grid-cols-[44px_1.1fr_1.4fr_66px_110px_100px_150px_64px] items-center gap-2.5";
  const cellInput =
    "h-9 w-full rounded-lg border border-[#DFE2E8] bg-white px-2.5 text-[13px] text-[#1F2433] outline-none focus:border-[#6B4EFF]";
  const fieldLabel = "text-xs font-bold text-[#9097A8]";
  const fieldBox =
    "flex h-10 items-center gap-2.5 rounded-[9px] border border-[#DFE2E8] bg-white px-3";
  // No left padding here: Tailwind puts .pl-11 BEFORE .pl-3 in the CSS, so
  // "pl-3 pl-11" stayed 12px and the assignee's avatar covered the name
  // (Roadmap 1854). Each select adds its own.
  const selectClass =
    "h-10 w-full cursor-pointer appearance-none rounded-[9px] border border-[#DFE2E8] bg-white pr-8 text-[13.5px] text-[#1F2433] outline-none focus:border-[#6B4EFF]";

  // Second row of a line: which work item it is for (optional).
  const renderWorkLink = (item: InvoiceFormItem) => {
    if (item.invoiceType === "billedItem") {
      return (
        <div
          className={
            "mt-1.5 flex items-center gap-1.5 pl-[54px] text-[12px] text-[#6B7185]"
          }
        >
          <LuBriefcase size={13} className={"text-[#9097A8]"} />
          <span className={"font-semibold text-[#3A4256]"}>
            {item.workName || "Work item"}
          </span>
          <span>· from tracked time</span>
        </div>
      );
    }
    const options = workOptionsFor(linkableWorks, item);
    return (
      <div className={"mt-1.5 flex items-center gap-2 pl-[54px]"}>
        <LuBriefcase size={13} className={"shrink-0 text-[#9097A8]"} />
        <span className={"shrink-0 text-[12px] font-semibold text-[#9097A8]"}>
          Work item
        </span>
        <div className={"relative w-full max-w-[420px]"}>
          <select
            aria-label={"Work item"}
            className={classNames(
              "h-8 w-full cursor-pointer appearance-none rounded-lg border bg-white pl-2.5 pr-7 text-[12.5px] outline-none focus:border-[#6B4EFF]",
              item.workId
                ? "border-[#CFC6FF] text-[#1F2433]"
                : "border-[#DFE2E8] text-[#9097A8]",
            )}
            value={item.workId || ""}
            onChange={(event) =>
              updateItem(item.clientRowId, () =>
                linkLineToWork(event.target.value, linkableWorks),
              )
            }
          >
            <option value={""}>
              {options.length
                ? "Not linked to a work item"
                : "No work items for this client"}
            </option>
            {options.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </select>
          <LuChevronDown
            size={12}
            className={
              "pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-[#9AA0B0]"
            }
          />
        </div>
      </div>
    );
  };

  const tableRows = data.map((item, index) => (
    <Draggable
      key={item.clientRowId}
      draggableId={item.clientRowId}
      index={index}
    >
      {(provided) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          className={"border-b border-[#F4F5F7] bg-white py-3"}
        >
          <div className={lineGrid}>
            <span
              {...provided.dragHandleProps}
              title={"Drag to reorder"}
              className={
                "flex cursor-move items-center gap-0.5 text-[13.5px] text-[#9097A8]"
              }
            >
              <RxDragHandleDots2 size={16} className={"text-[#C6CBD6]"} />
              {index + 1}
            </span>

            <div className={"relative"}>
              <select
                aria-label={"Service"}
                className={classNames(
                  cellInput,
                  "cursor-pointer appearance-none pr-7",
                )}
                value={item.service}
                onChange={(event) => {
                  const selectedService = sortedServices.find(
                    (service) => service.id === event.target.value,
                  );

                  updateItem(item.clientRowId, (invoiceItem) => ({
                    service: event.target.value,
                    description: selectedService?.description || "",
                    rate:
                      invoiceItem.invoiceType === "billedItem"
                        ? invoiceItem.rate
                        : selectedService?.rate?.toString() || "",
                    amount:
                      invoiceItem.invoiceType === "billedItem"
                        ? invoiceItem.amount
                        : invoiceItem.qty * (selectedService?.rate || 0),
                  }));
                }}
              >
                {sortedServices.map((service) => (
                  <option key={service.id} value={service.id}>
                    {service.name}
                  </option>
                ))}
              </select>
              <LuChevronDown
                size={12}
                className={
                  "pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-[#9AA0B0]"
                }
              />
            </div>

            <input
              type={"text"}
              aria-label={"Description"}
              placeholder={"Description (optional)"}
              className={classNames(cellInput, "placeholder:text-[#9AA0B0]")}
              value={item.description}
              maxLength={200}
              onChange={(event) =>
                updateItem(item.clientRowId, () => ({
                  description: event.target.value,
                }))
              }
            />

            <input
              type={"text"}
              aria-label={"Quantity"}
              className={classNames(cellInput, "text-center")}
              value={item.qty}
              onChange={(event) => {
                const value = event.target.value;

                updateItem(item.clientRowId, (invoiceItem) => ({
                  qty: /^[0-9]*$/.test(value) ? Number(value) : invoiceItem.qty,
                  amount: Number(value) * parseMoney(invoiceItem.rate),
                }));
              }}
            />

            <CustomCurrencyInput
              value={item.rate}
              className={
                "!h-9 !rounded-lg !border-[#DFE2E8] !px-2.5 !text-right !text-[13px] tabular-nums focus:!border-[#6B4EFF] focus:!outline-none"
              }
              setValue={(value) =>
                updateItem(item.clientRowId, (invoiceItem) => ({
                  rate: value,
                  amount: parseMoney(value) * invoiceItem.qty,
                }))
              }
            />

            <span
              className={"text-right text-[13px] tabular-nums text-[#3A4256]"}
            >
              ${formatMoney(item.amount)}
            </span>

            <div className={"relative"}>
              <select
                aria-label={"Sales tax"}
                className={classNames(
                  cellInput,
                  "cursor-pointer appearance-none pr-7 text-[12.5px]",
                )}
                value={item.taxCodeId || taxRateValue?.id || ""}
                onChange={(event) => {
                  // BILL-039 (QA batch 17): only THIS line's tax code changes.
                  const taxCodeId = event.target.value;
                  updateItem(item.clientRowId, () => ({ taxCodeId }));
                }}
              >
                {taxRateData.map((taxRate) => (
                  <option value={taxRate.id} key={taxRate.id}>
                    {taxRate.taxCodeName}
                    {taxRate.isSyncedWithQBO && " (synced)"}
                  </option>
                ))}
              </select>
              <LuChevronDown
                size={12}
                className={
                  "pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-[#9AA0B0]"
                }
              />
            </div>

            <div className={"flex items-center justify-center gap-0.5"}>
              <button
                type={"button"}
                aria-label={"Remove line"}
                disabled={data.length <= 1}
                onClick={() => handleDeleteItem(item.clientRowId)}
                className={
                  "flex h-8 w-8 items-center justify-center rounded-[7px] text-[#E5484D] hover:bg-[#FDECEC] disabled:cursor-not-allowed disabled:text-[#D5D8DF] disabled:hover:bg-transparent"
                }
              >
                <LuTrash2 size={16} />
              </button>

              {item.invoiceType === "billedItem" && (
                <>
                  <button
                    type={"button"}
                    aria-label={"View billed time"}
                    onClick={() => {
                      setViewWork(true);
                      setWorkItem(item);
                    }}
                    className={
                      "flex h-8 w-8 items-center justify-center rounded-[7px] text-[#6B4EFF] hover:bg-[#F4F2FF]"
                    }
                  >
                    <IoTimeOutline size={18} />
                  </button>

                  {viewWork && workItem?.clientRowId === item.clientRowId && (
                    <ViewWork
                      assignedUser={workItem?.assignedUser}
                      qty={workItem?.qty}
                      workType={workItem?.workType}
                      workName={workItem?.workName}
                      billedWorks={existingInvoiceItems}
                      billedWork={workItem}
                      isActive={viewWork}
                      setCollapsedItems={setCollapsedItems}
                      collapsedItems={collapsedItems}
                      setViewWork={setViewWork}
                      totalRate={totalRate}
                    />
                  )}
                </>
              )}
            </div>
          </div>
          {renderWorkLink(item)}
        </div>
      )}
    </Draggable>
  ));

  const assignee = users.find((item) => item.id === selectedUserId);

  return (
    <>
      <div
        className={classNames(
          BILLING_CARD,
          "relative z-[5] mb-5 px-7 py-[26px]",
        )}
      >
        <div className={"mb-5 flex items-center gap-3"}>
          <h2
            className={
              "m-0 text-[22px] font-extrabold tracking-[-0.5px] text-[#1F2433]"
            }
          >
            {title}
          </h2>
          <input
            type={"number"}
            min={0}
            step={1}
            aria-label={"Invoice number"}
            className={
              "h-[34px] w-[90px] rounded-lg border border-[#DFE2E8] px-3 text-center text-[13px] font-semibold text-[#6B7185] outline-none focus:border-[#6B4EFF]"
            }
            value={invoiceNo}
            onChange={(event) =>
              setInvoiceNo(
                Math.max(0, Math.floor(event.target.valueAsNumber || 0)),
              )
            }
          />
        </div>

        <div className={"mb-2 text-[12.5px] font-bold text-[#3A4256]"}>
          Client
        </div>
        <InvoiceClientPicker
          selected={selectedContacts}
          options={mergedContactOptions}
          search={search}
          onSearch={setSearch}
          isLoading={isLoadingContactsFirstPage}
          isLoadingMore={isLoadingMoreContacts}
          onLoadMore={handleLoadMoreContacts}
          onChange={handleSelectedContactsChange}
        />

        <div className={"mt-[22px] flex flex-wrap gap-[26px]"}>
          <label className={"flex flex-col gap-[7px]"}>
            <span className={fieldLabel}>Assignee</span>
            <div className={"relative min-w-[190px]"}>
              {assignee && (
                <span
                  className={
                    "pointer-events-none absolute left-3 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full text-[10px] font-bold text-white"
                  }
                  style={{
                    backgroundColor: assignee.userProfile?.color || "#1E3A8A",
                  }}
                >
                  {getInitials(
                    assignee.userProfile?.firstName || "",
                    assignee.userProfile?.lastName || "",
                  )}
                </span>
              )}
              <select
                className={classNames(selectClass, assignee ? "pl-11" : "pl-3")}
                value={selectedUserId || ""}
                onChange={(event) =>
                  setSelectedUserId(event.target.value || null)
                }
              >
                <option value={""}>None</option>
                {users.map((item) => (
                  <option key={item.id} value={item.id}>
                    {`${item.userProfile?.firstName || ""} ${
                      item.userProfile?.middleName || ""
                    } ${item.userProfile?.lastName || ""}`.trim()}
                  </option>
                ))}
              </select>
              <LuChevronDown
                size={13}
                className={
                  "pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[#9AA0B0]"
                }
              />
            </div>
          </label>

          <div className={"flex flex-col gap-[7px]"}>
            <span className={fieldLabel}>Invoice Date</span>
            <div className={classNames(fieldBox, "w-[170px] overflow-hidden")}>
              <div className={"min-w-0 flex-1"}>
                <StyledDatePicker
                  value={invoiceDate}
                  format={BILLING_DATE_PICKER_FORMAT}
                  acceptTypedDates
                  onInvalidTyped={() =>
                    setDateProblem({ field: "invoice", text: INVALID_DATE })
                  }
                  styleType={"ghost"}
                  fontSize={"13.5px"}
                  width={"112px"}
                  onChange={(date) => {
                    setDateProblem(null);
                    const nextInvoiceDate = transformDateToUTC(date);

                    setInvoiceDate(nextInvoiceDate);
                    setShowDueDateError(false);
                    setDueDate(
                      dueDateAfterTermsChange(
                        terms,
                        term,
                        nextInvoiceDate,
                        dueDate,
                      ),
                    );
                  }}
                />
              </div>
              <LuCalendar size={15} className={"shrink-0 text-[#9AA0B0]"} />
            </div>
            {dateProblem?.field === "invoice" && (
              <span
                role={"alert"}
                className={"max-w-[170px] text-[12px] text-[#B42318]"}>
                {dateProblem.text}
              </span>
            )}
          </div>

          <label className={"flex flex-col gap-[7px]"}>
            <span className={fieldLabel}>Terms</span>
            <div className={"relative min-w-[150px]"}>
              <select
                className={classNames(selectClass, "pl-3")}
                value={term}
                onChange={(event) => {
                  setTerm(event.target.value);
                  setShowDueDateError(false);
                  setDueDate(
                    dueDateAfterTermsChange(
                      terms,
                      event.target.value,
                      invoiceDate,
                      dueDate,
                    ),
                  );
                }}
              >
                {/* No firm "Custom" term: a hand-picked due date shows as
                    "Custom date" (no term saved), QA batch 11. */}
                {!hasCustomTerm(terms) && (
                  <option value={""}>Custom date</option>
                )}
                {terms.map((item) => (
                  <option value={item.id} key={item.id}>
                    {item.termName}
                  </option>
                ))}
              </select>
              <LuChevronDown
                size={13}
                className={
                  "pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[#9AA0B0]"
                }
              />
            </div>
          </label>

          <div className={"flex flex-col gap-[7px]"}>
            <span className={fieldLabel}>Due Date</span>
            <div className={classNames(fieldBox, "w-[170px] overflow-hidden")}>
              <div className={"min-w-0 flex-1"}>
                <StyledDatePicker
                  value={dueDate}
                  minDate={invoiceDate}
                  format={BILLING_DATE_PICKER_FORMAT}
                  acceptTypedDates
                  onInvalidTyped={(reason) =>
                    setDateProblem({
                      field: "due",
                      text:
                        reason === "out-of-range"
                          ? DUE_DATE_ERROR
                          : INVALID_DATE,
                    })
                  }
                  onOutOfRange={() => undefined}
                  styleType={"ghost"}
                  fontSize={"13.5px"}
                  width={"112px"}
                  onChange={(date) => {
                    setDateProblem(null);
                    const nextDueDate = transformDateToUTC(date);
                    setShowDueDateError(false);
                    setDueDate(nextDueDate);
                    setTerm(
                      termAfterDueDateChange(
                        terms,
                        term,
                        invoiceDate,
                        nextDueDate,
                      ),
                    );
                  }}
                />
              </div>
              <LuCalendar size={15} className={"shrink-0 text-[#9AA0B0]"} />
            </div>
            {dateProblem?.field === "due" && (
              <span
                role={"alert"}
                className={"max-w-[170px] text-[12px] text-[#B42318]"}>
                {dateProblem.text}
              </span>
            )}
            {showDueDateError &&
              isDueDateBeforeInvoiceDate(invoiceDate, dueDate) && (
                <span
                  role={"alert"}
                  className={"max-w-[160px] text-[12px] text-[#B42318]"}>
                  {DUE_DATE_ERROR}
                </span>
              )}
          </div>
        </div>
      </div>

      <div className={classNames(BILLING_CARD, "px-7 pb-7 pt-6")}>
        <div className={"mb-4 flex items-center justify-between"}>
          <h3 className={"m-0 text-[17px] font-extrabold text-[#1F2433]"}>
            Service(s)
          </h3>
          <label className={"flex cursor-pointer items-center gap-[9px]"}>
            <span className={"text-[13px] text-[#6B7185]"}>
              Inclusive of Tax
            </span>
            <button
              type={"button"}
              role={"switch"}
              aria-checked={isIncludeTax}
              aria-label={"Inclusive of Tax"}
              onClick={() => handleIncludeTaxChange(!isIncludeTax)}
              className={classNames(
                "relative h-[22px] w-10 rounded-full transition-colors",
                isIncludeTax ? "bg-[#6B4EFF]" : "bg-[#DCE0E8]",
              )}
            >
              <span
                className={classNames(
                  "absolute top-0.5 h-[18px] w-[18px] rounded-full bg-white shadow-[0_1px_3px_rgba(0,0,0,0.2)] transition-all",
                  isIncludeTax ? "left-[20px]" : "left-0.5",
                )}
              />
            </button>
          </label>
        </div>

        <div
          className={classNames(
            lineGrid,
            "rounded bg-[#EAECEF] px-3 py-[11px] text-[12.5px] font-bold text-[#727A8A]",
          )}
        >
          <span>#</span>
          <span>Service</span>
          <span>Description</span>
          <span>Qty</span>
          <span className={"text-right"}>Rate</span>
          <span className={"text-right"}>Amount</span>
          <span>Sales Tax</span>
          <span />
        </div>

        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId={"invoice-items"}>
            {(provided) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className={"px-3"}
              >
                {tableRows}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>

        {isBillableTime && !isLoadingWorks && !works?.length && (
          <div
            role={"alert"}
            className={
              "mt-3 rounded-lg border border-[#F5D0D0] bg-[#FDF3F3] px-3 py-2 text-[13px] text-[#B42318]"
            }
          >
            There are no pending time entries
          </div>
        )}

        <div className={"mt-4 flex gap-3"}>
          <button
            type={"button"}
            onClick={handleAddItem}
            className={
              "inline-flex h-[34px] items-center gap-[7px] rounded-lg border border-[#C9BEFF] bg-white px-3.5 text-[12.5px] font-bold text-[#6B4EFF] hover:bg-[#F4F2FF]"
            }
          >
            + Add Line Item
          </button>
          <button
            type={"button"}
            onClick={() => setIsBillableTime(true)}
            className={
              "inline-flex h-[34px] items-center gap-[7px] rounded-lg border border-[#C9BEFF] bg-white px-3.5 text-[12.5px] font-bold text-[#6B4EFF] hover:bg-[#F4F2FF]"
            }
          >
            + Add Billable time
          </button>
        </div>

        <div
          className={"mt-[26px] grid grid-cols-[1fr_320px] items-start gap-10"}
        >
          <label className={"block"}>
            <span
              className={"mb-[9px] block text-[13px] font-bold text-[#3A4256]"}
            >
              Notes
            </span>
            <textarea
              className={
                "min-h-[80px] w-full max-w-[360px] resize-y rounded-[10px] border border-[#EAECEF] px-3.5 py-3 text-[13px] text-[#1F2433] outline-none placeholder:text-[#9AA0B0] focus:border-[#6B4EFF]"
              }
              placeholder={"Add notes for this invoice..."}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
            />
          </label>

          <div>
            <div className={"mb-3.5 text-[17px] font-extrabold text-[#1F2433]"}>
              Summary
            </div>
            <div
              className={
                "mb-2.5 flex justify-between text-[13.5px] text-[#3A4256]"
              }
            >
              <span className={"text-[#6B7185]"}>Subtotal</span>
              <span className={"tabular-nums"}>${formatMoney(subtotal)}</span>
            </div>

            <div
              className={
                "mb-2.5 flex items-center justify-between text-[13.5px] text-[#3A4256]"
              }
            >
              <span className={"text-[#6B7185]"}>Discount</span>
              <span className={"inline-flex items-center gap-1.5"}>
                <span className={"relative"}>
                  <select
                    aria-label={"Discount type"}
                    value={discountType}
                    className={
                      "h-[30px] cursor-pointer appearance-none rounded-[7px] border border-[#DFE2E8] bg-white pl-2 pr-6 text-[12.5px] text-[#3A4256] outline-none focus:border-[#6B4EFF]"
                    }
                    onChange={(event) =>
                      setDiscountType(event.target.value as "$" | "%")
                    }
                  >
                    <option value={"$"}>$</option>
                    <option value={"%"}>%</option>
                  </select>
                  <LuChevronDown
                    size={10}
                    className={
                      "pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[#9AA0B0]"
                    }
                  />
                </span>
                <span className={"w-[64px]"}>
                  <CustomCurrencyInput
                    allowNegative={false}
                    value={discount}
                    setValue={setDiscount}
                    prefix={""}
                    className={
                      "!h-[30px] !min-h-0 !rounded-[7px] !border-[#DFE2E8] !px-2 !text-right !text-[12.5px] focus:!border-[#6B4EFF] focus:!outline-none"
                    }
                  />
                </span>
                {discountType === "%" && (
                  <span className={"tabular-nums text-[#6B7185]"}>
                    ${formatMoney(discountValue)}
                  </span>
                )}
              </span>
            </div>

            {!!taxRows.length && (
              <>
                <div className={"mb-1.5 mt-3 text-[12.5px] text-[#9097A8]"}>
                  Sales Tax
                </div>
                {taxRows.map((taxRow) => (
                  <div
                    key={taxRow.key}
                    className={
                      "mb-1.5 flex justify-between pl-2.5 text-[13px] text-[#3A4256]"
                    }
                  >
                    <span className={"text-[#6B7185]"}>
                      {taxRow.taxName} ({taxRow.taxRate}%)
                    </span>
                    <span className={"tabular-nums"}>
                      ${formatMoney(taxRow.amount)}
                    </span>
                  </div>
                ))}
                {isIncludeTax && (
                  <div className={"mb-1.5 pl-2.5 text-xs text-[#9097A8]"}>
                    Included in the amounts (on ${formatMoney(taxBaseAmount)})
                  </div>
                )}
              </>
            )}

            <div
              className={
                "mt-3.5 flex items-center justify-between border-t border-[#EAECEF] pt-4"
              }
            >
              <span className={"text-base font-extrabold text-[#1F2433]"}>
                Total
              </span>
              <span
                className={
                  "text-[19px] font-extrabold tabular-nums text-[#1F2433]"
                }
              >
                ${formatMoney(total)}
              </span>
            </div>

            {mode === "edit" && (
              <>
                <div
                  className={
                    "mt-2 flex justify-between text-[13px] text-[#6B7185]"
                  }
                >
                  <span>Payment Received</span>
                  <span className={"tabular-nums"}>
                    ${formatMoney(paymentsReceived)}
                  </span>
                </div>
                <div className={"mt-3 flex items-center justify-between"}>
                  <span className={"text-base font-extrabold text-[#1F2433]"}>
                    Balance Due
                  </span>
                  <span
                    className={
                      "text-xl font-extrabold tabular-nums text-[#6B4EFF]"
                    }
                  >
                    ${formatMoney(balanceDue)}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>

        {amountProblem && (
          <div
            role={"alert"}
            className={
              "mt-4 rounded-lg border border-[#F5D0D0] bg-[#FDF3F3] px-3 py-2 text-right text-[13px] text-[#B42318]"
            }
          >
            {amountProblem}
          </div>
        )}

        <div className={"mt-[26px] flex items-center justify-end gap-3.5"}>
          <button
            type={"button"}
            onClick={onCancel}
            className={classNames(
              BILLING_SECONDARY_BUTTON,
              "h-[42px] px-[22px]",
            )}
          >
            Cancel
          </button>

          {showSaveAndPay ? (
            <div ref={optionsRef} className={"relative flex"}>
              <button
                type={"button"}
                onClick={() => submit("close")}
                disabled={isSubmitDisabled}
                className={
                  "h-[42px] rounded-l-[9px] bg-[#6B4EFF] px-[22px] text-[13.5px] font-bold text-white hover:bg-[#5A3EEB] disabled:cursor-not-allowed disabled:bg-[#C3C8D4]"
                }
              >
                {isSubmitting ? "Saving..." : submitLabel}
              </button>
              <button
                type={"button"}
                aria-label={"More save options"}
                disabled={isSubmitDisabled}
                onClick={(event) => {
                  event.stopPropagation();
                  setIsOpenSaveMenu((open) => !open);
                }}
                className={
                  "flex h-[42px] w-[38px] items-center justify-center rounded-r-[9px] border-l border-white/30 bg-[#6B4EFF] text-white hover:bg-[#5A3EEB] disabled:cursor-not-allowed disabled:bg-[#C3C8D4]"
                }
              >
                <LuChevronDown size={14} />
              </button>

              {isOpenSaveMenu && (
                <div
                  className={
                    "absolute bottom-[calc(100%+6px)] right-0 z-[21] w-[230px] rounded-[10px] border border-[#E8E8EC] bg-white p-1.5 shadow-[0_14px_36px_rgba(20,22,34,0.18)]"
                  }
                >
                  <button
                    type={"button"}
                    onClick={() => {
                      setIsOpenSaveMenu(false);
                      void submit("pay");
                    }}
                    className={
                      "flex w-full items-center gap-2.5 rounded-lg px-[11px] py-[9px] text-left text-[13px] font-semibold text-[#3A4256] hover:bg-[#F4F2FF]"
                    }
                  >
                    <LuCreditCard size={15} className={"text-[#6B4EFF]"} />
                    Create &amp; Record Payment
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button
              type={"button"}
              onClick={() => submit("close")}
              disabled={isSubmitDisabled}
              className={classNames(
                BILLING_PRIMARY_BUTTON,
                "h-[42px] rounded-[9px] px-[22px] shadow-none",
              )}
            >
              {isSubmitting ? "Saving..." : submitLabel}
            </button>
          )}
        </div>
      </div>

      {footer}

      {isBillableTime && !!works?.length && (
        <BillableTime
          isLoading={isLoadingWorks}
          currentContact={currentContact}
          collapsedItems={collapsedItems}
          setCollapsedItems={setCollapsedItems}
          works={works}
          billedWorks={billedWorks}
          onAdd={handleAddWorkItems}
          onClose={() => setIsBillableTime(false)}
          isActive={isBillableTime}
        />
      )}

      {duplicateNumberSave && (
        <SettingsConfirmModal
          onClose={() => setDuplicateNumberSave(null)}
          onConfirm={() => {
            const option = duplicateNumberSave;

            setDuplicateNumberSave(null);
            void submit(option, true);
          }}
          icon={<WarningExclaimSvg sizeCx={"h-6 w-6"} />}
          title={"Invoice number already in use"}
          description={`Invoice no. ${invoiceNo} is already used by another invoice. Do you want to use the same number anyway?`}
          confirmText={"Use this number"}
          cancelText={"Change number"}
          variant={"warning"}
        />
      )}

      <ConfirmNavigation when={isDirty && !isSubmitting && !isSaving} />
    </>
  );
};
